import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  MenuItem,
  Select,
  Snackbar,
  Grid,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

// FieldInput component (unchanged)
const FieldInput = ({
  field,
  onFieldChange,
  showValue = true,
  size = "small",
  sx,
  error,
  setError,
}) => {
  const handleKeyChange = (value) => {
    if (value.includes(" ")) {
      setError("Field name cannot contain spaces");
    } else {
      setError(null);
      onFieldChange("key", value);
    }
  };

  return (
    <Grid container spacing={2} sx={{ ...sx, marginTop: "1px" }}>
      <Grid item xs={4}>
        <TextField
          fullWidth
          label="Field Name"
          value={field.key || ""}
          onChange={(e) => handleKeyChange(e.target.value)}
          size={size}
          error={!!error}
          helperText={error}
        />
      </Grid>
      <Grid item xs={4}>
        <Select
          fullWidth
          value={field.type}
          onChange={(e) => onFieldChange("type", e.target.value)}
          size={size}
        >
          <MenuItem value="string">String</MenuItem>
          <MenuItem value="number">Number</MenuItem>
          <MenuItem value="boolean">Boolean</MenuItem>
          <MenuItem value="object">Object</MenuItem>
          <MenuItem value="array">Array</MenuItem>
        </Select>
      </Grid>
      {showValue && field.type !== "object" && field.type !== "array" && (
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Value"
            value={field.value || ""}
            onChange={(e) => onFieldChange("value", e.target.value)}
            size={size}
          />
        </Grid>
      )}
    </Grid>
  );
};

// RecursiveFieldEditor component (unchanged)
const RecursiveFieldEditor = ({
  field,
  onFieldChange,
  onDeleteField,
  onAddNestedField,
  depth = 0,
  parentIndex = null,
  index = null,
  arrayKey = null,
}) => {
  const [localField, setLocalField] = useState(field);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLocalField(field);
  }, [field]);

  const handleFieldChange = (key, value) => {
    const updatedField = { ...localField, [key]: value };
    setLocalField(updatedField);
    onFieldChange(updatedField, parentIndex, index);
  };

  return (
    <Grid spacing={2} sx={{ ml: depth * 2, mt: 1 }}>
      <Grid item xs={12} container justifyContent={"center"} spacing={1}>
        {arrayKey && (
          <Grid item>
            <Typography sx={{ fontWeight: 600 }}>{arrayKey}:</Typography>
          </Grid>
        )}
        <Grid item xs={10}>
          <FieldInput
            field={localField}
            onFieldChange={handleFieldChange}
            error={error}
            setError={setError}
          />
        </Grid>
        <Grid sx={{ mt: 2 }} item xs={2}>
          <IconButton
            size="small"
            onClick={() => onDeleteField(parentIndex, index)}
          >
            <DeleteIcon />
          </IconButton>
          {(localField.type === "object" || localField.type === "array") && (
            <IconButton
              size="small"
              onClick={() => onAddNestedField(parentIndex, index)}
            >
              <AddIcon />
            </IconButton>
          )}
        </Grid>
      </Grid>
      {localField.type === "array" && (
        <Box sx={{ ml: 2, mt: 1 }}>
          {localField.arrayObjects &&
            localField.arrayObjects.map((arrayObject, arrayObjectIndex) => (
              <Box
                key={arrayObjectIndex}
                sx={{
                  border: "1px solid grey",
                  borderRadius: 1,
                  p: 1,
                  mb: 1,
                }}
              >
                <Typography sx={{ mb: 1, fontWeight: 600 }}>
                  {localField.key
                    ? `${localField.key} ${arrayObjectIndex + 1}`
                    : `Object ${arrayObjectIndex + 1}`}
                </Typography>
                {arrayObject.map((nestedField, nestedIndex) => (
                  <RecursiveFieldEditor
                    key={nestedIndex}
                    field={nestedField}
                    onFieldChange={(updatedField, p, i) => {
                      const updatedArrayObjects = [
                        ...(localField.arrayObjects || []),
                      ];
                      updatedArrayObjects[arrayObjectIndex][nestedIndex] =
                        updatedField;
                      const updatedLocalField = {
                        ...localField,
                        arrayObjects: updatedArrayObjects,
                      };
                      setLocalField(updatedLocalField);
                      onFieldChange(updatedLocalField, parentIndex, index);
                    }}
                    onDeleteField={() => {
                      const updatedArrayObjects = [
                        ...(localField.arrayObjects || []),
                      ];
                      updatedArrayObjects[arrayObjectIndex] =
                        updatedArrayObjects[arrayObjectIndex].filter(
                          (_, i) => i !== nestedIndex
                        );
                      const filteredArrayObjects = updatedArrayObjects.filter(
                        (obj) => obj.length > 0
                      );
                      const updatedLocalField = {
                        ...localField,
                        arrayObjects: filteredArrayObjects,
                      };
                      setLocalField(updatedLocalField);
                      onFieldChange(updatedLocalField, parentIndex, index);
                    }}
                    onAddNestedField={() => {
                      const updatedArrayObjects = [
                        ...(localField.arrayObjects || []),
                      ];
                      updatedArrayObjects[arrayObjectIndex].push({
                        key: "",
                        type: "string",
                        value: "",
                        nestedFields: [],
                      });
                      const updatedLocalField = {
                        ...localField,
                        arrayObjects: updatedArrayObjects,
                      };
                      setLocalField(updatedLocalField);
                      onFieldChange(updatedLocalField, parentIndex, index);
                    }}
                    depth={depth + 1}
                    arrayKey={`Object ${arrayObjectIndex + 1}`}
                  />
                ))}
                <Button
                  size="small"
                  sx={{ mt: 1, mr: 1 }}
                  onClick={() => {
                    const updatedArrayObjects = [
                      ...(localField.arrayObjects || []),
                    ];
                    updatedArrayObjects[arrayObjectIndex].push({
                      key: "",
                      type: "string",
                      value: "",
                      nestedFields: [],
                    });
                    const updatedLocalField = {
                      ...localField,
                      arrayObjects: updatedArrayObjects,
                    };
                    setLocalField(updatedLocalField);
                    onFieldChange(updatedLocalField, parentIndex, index);
                  }}
                >
                  Add Field to Object
                </Button>
              </Box>
            ))}
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={() => {
              const updatedLocalField = {
                ...localField,
                arrayObjects: [
                  ...(localField.arrayObjects || []),
                  [{ key: "", type: "string", value: "", nestedFields: [] }],
                ],
              };
              setLocalField(updatedLocalField);
              onFieldChange(updatedLocalField, parentIndex, index);
            }}
          >
            Add New Object
          </Button>
        </Box>
      )}
      {localField.type === "object" && (
        <Box sx={{ ml: 2, width: "100%" }}>
          {(localField.nestedFields || []).map((nestedField, nestedIndex) => (
            <RecursiveFieldEditor
              key={nestedIndex}
              field={nestedField}
              onFieldChange={(updatedField, p, i) => {
                const updatedNestedFields = [
                  ...(localField.nestedFields || []),
                ];
                updatedNestedFields[nestedIndex] = updatedField;
                const updatedLocalField = {
                  ...localField,
                  nestedFields: updatedNestedFields,
                };
                setLocalField(updatedLocalField);
                onFieldChange(updatedLocalField, parentIndex, index);
              }}
              onDeleteField={() => {
                const updatedNestedFields = localField.nestedFields.filter(
                  (_, idx) => idx !== nestedIndex
                );
                const updatedField = {
                  ...localField,
                  nestedFields: updatedNestedFields,
                };
                setLocalField(updatedField);
                onFieldChange(updatedField, parentIndex, index);
              }}
              onAddNestedField={() => {
                const updatedNestedFields = [
                  ...(localField.nestedFields || []),
                  { key: "", type: "string", value: "", nestedFields: [] },
                ];
                const updatedField = {
                  ...localField,
                  nestedFields: updatedNestedFields,
                };
                setLocalField(updatedField);
                onFieldChange(updatedField, parentIndex, index);
              }}
              depth={depth + 1}
              parentIndex={parentIndex}
              index={index}
            />
          ))}
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={() => {
              const updatedLocalField = {
                ...localField,
                nestedFields: [
                  ...(localField.nestedFields || []),
                  { key: "", type: "string", value: "", nestedFields: [] },
                ],
              };
              setLocalField(updatedLocalField);
              onFieldChange(updatedLocalField, parentIndex, index);
            }}
          >
            Add Field
          </Button>
        </Box>
      )}
    </Grid>
  );
};

// Main component with new features
const EditableHierarchicalJsonViewer = ({
  open,
  onClose,
  data,
  onSaveWorkFlowData,
  title = "Update Data",
  step,
  hiddenKeys = [
    "__v",
    "createdAt",
    "updatedAt",
    "_id",
    "created_by",
    "client_id",
    "order_id",
    "order_number",
    "order_date",
    "fulfillment_status",
    "subtotal_price",
    "total_line_items_price",
    "total_price",
    "status",
    "customer",
    "oms_provider",
    "publishToClient",
    "shipments",
    "shipping_methods",
    "manifest_delay_notified",
    "publish_to_historical",
    "type",
    "sub_type",
    "shipment_references",
    "error",
    "shipping_lines",
    "published_to_historical",
    "contact_email",
    "id",
    "admin_graphql_api_id",
    "app_id",
    "browser_ip",
    "buyer_accepts_marketing",
    "cancel_reason",
    "cancelled_at",
    "cart_token",
    "checkout_id",
    "checkout_token",
    "client_details",
    "closed_at",
    "confirmation_number",
    "confirmed",
    "currency",
    "current_shipping_price_set",
    "current_subtotal_price",
    "current_subtotal_price_set",
    "current_total_additional_fees_set",
    "current_total_discounts",
    "current_total_discounts_set",
    "current_total_duties_set",
    "current_total_price",
    "current_total_price_set",
    "current_total_tax",
    "current_total_tax_set",
    "customer_locale",
    "device_id",
    "discount_codes",
    "duties_included",
    "email",
    "estimated_taxes",
    "financial_status",
    "landing_site",
    "landing_site_ref",
    "location_id",
    "merchant_business_entity_id",
    "merchant_of_record_app_id",
    "name",
    "note",
    "note_attributes",
    "number",
    "order_status_url",
    "original_total_additional_fees_set",
    "original_total_duties_set",
    "payment_gateway_names",
    "phone",
    "po_number",
    "presentment_currency",
    "processed_at",
    "reference",
    "referring_site",
    "source_identifier",
    "source_name",
    "source_url",
    "subtotal_price_set",
    "tags",
    "tax_exempt",
    "tax_lines",
    "taxes_included",
    "test",
    "token",
    "total_cash_rounding_payment_adjustment_set",
    "total_cash_rounding_refund_adjustment_set",
    "total_discounts",
    "total_discounts_set",
    "total_line_items_price_set",
    "product_exists",
    "product_id",
    "fulfillment_service",
    "price",
    "variant_id",
    "bundle",
    "child",
    "exclude_from_shipping",
    "variant_title",
    "total_discount",
    "total_discount_set",
    "latitude",
    "longitude",
    "parent",
    "step",
    "response",
    "warehouse",
  ],
  disabledKeys = [], // New prop for disabling keys
}) => {
  const [editableData, setEditableData] = useState({});
  const [expandedKeys, setExpandedKeys] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);
  const [addingField, setAddingField] = useState(null);
  const [newField, setNewField] = useState({
    key: "",
    type: "string",
    value: "",
  });
  const [newObjectFields, setNewObjectFields] = useState([]);
  const [newArrayObjects, setNewArrayObjects] = useState([]);
  const [fieldError, setFieldError] = useState(null);
  const inputRefs = useRef({});
  const initialDataRef = useRef(null);

  useEffect(() => {
    if (open && data) {
      setEditableData(
        JSON.parse(JSON.stringify(data.input ? { input: data.input } : data))
      );
      initialDataRef.current = JSON.parse(
        JSON.stringify(data.input ? { input: data.input } : data)
      );
    }
  }, [open, data]);

  const handleAddRootField = () => {
    setAddingField("root");
    setNewField({ key: "", type: "string", value: "" });
    setNewObjectFields([]);
    setNewArrayObjects([]);
    setFieldError(null);
  };

  const processFields = (fields) => {
    const result = Array.isArray(fields) ? [] : {};

    fields.forEach(({ key, value, type, nestedFields, arrayObjects }) => {
      if (type !== "object" && type !== "array") {
        const processedValue =
          type === "number"
            ? Number(value)
            : type === "boolean"
            ? value === "true"
            : value;
        if (Array.isArray(result)) {
          result.push(processedValue);
        } else if (key) {
          result[key] = processedValue;
        }
      } else if (type === "object") {
        const processedObject = nestedFields ? processFields(nestedFields) : {};
        if (Array.isArray(result)) {
          result.push(key ? { [key]: processedObject } : processedObject);
        } else if (key) {
          result[key] = processedObject;
        }
      } else if (type === "array") {
        let processedArray;
        if (arrayObjects && arrayObjects.length > 0) {
          processedArray = arrayObjects.map((objectFields) =>
            processFields(objectFields)
          );
        } else if (nestedFields && nestedFields.length > 0) {
          processedArray = nestedFields.map((field) => processFields([field]));
        } else {
          processedArray = [];
        }
        if (Array.isArray(result)) {
          result.push(key ? { [key]: processedArray } : processedArray);
        } else if (key) {
          result[key] = processedArray;
        }
      }
    });

    return result;
  };

  const handleAddField = (path) => {
    setAddingField(path);
    setNewField({ key: "", type: "string", value: "" });
    setNewObjectFields([]);
    setNewArrayObjects([]);
    setFieldError(null);
  };

  const handleSaveField = () => {
    if (
      !newField.key &&
      newField.type !== "object" &&
      newField.type !== "array"
    )
      return;
    if (newField.key.includes(" ")) {
      setFieldError("Field name cannot contain spaces");
      return;
    }

    const updatedData = { ...editableData };

    if (addingField === "root") {
      if (newField.type === "object") {
        updatedData[newField.key] = processFields(newObjectFields);
      } else if (newField.type === "array") {
        updatedData[newField.key] = newArrayObjects.map((fields) =>
          processFields(fields)
        );
      } else {
        updatedData[newField.key] =
          newField.type === "number"
            ? Number(newField.value)
            : newField.type === "boolean"
            ? newField.value === "true"
            : newField.value;
      }
    } else {
      const keys = addingField.split(" > ");
      let current = updatedData;

      keys.forEach((key, index) => {
        if (index === keys.length - 1) {
          if (newField.type === "object") {
            const newObject = processFields(newObjectFields);
            if (newField.key) {
              if (Array.isArray(current[key])) {
                current[key].push({ [newField.key]: newObject });
              } else {
                current[key] = { ...current[key], [newField.key]: newObject };
              }
            } else {
              current[key] = { ...(current[key] || {}), ...newObject };
            }
          } else if (newField.type === "array") {
            const newArray = newArrayObjects.map((fields) =>
              processFields(fields)
            );
            if (newField.key) {
              if (Array.isArray(current[key])) {
                current[key].push(
                  ...newArray.map((item) => ({ [newField.key]: item }))
                );
              } else {
                current[key] = { ...current[key], [newField.key]: newArray };
              }
            } else {
              current[key] = [...(current[key] || []), ...newArray];
            }
          } else {
            if (Array.isArray(current[key])) {
              current[key].push({
                [newField.key]:
                  newField.type === "number"
                    ? Number(newField.value)
                    : newField.type === "boolean"
                    ? newField.value === "true"
                    : newField.value,
              });
            } else {
              current[key] = {
                ...current[key],
                [newField.key]:
                  newField.type === "number"
                    ? Number(newField.value)
                    : newField.type === "boolean"
                    ? newField.value === "true"
                    : newField.value,
              };
            }
          }
        } else {
          if (!current[key]) {
            current[key] = key === "clients" && index === 0 ? [] : {};
          }
          current = current[key];
        }
      });
    }
    setEditableData(updatedData);
    setAddingField(null);
    setNewObjectFields([]);
    setNewArrayObjects([]);
    setFieldError(null);
  };

  // Helper function to check if a key should be hidden
  const isKeyHidden = (path, key) => {
    const fullPath = path ? `${path} > ${key}` : key;
    return hiddenKeys.some((hiddenKey) => {
      if (hiddenKey.includes(".")) {
        const hiddenPathParts = hiddenKey.split(".");
        const currentPathParts = fullPath
          .split(" > ")
          .filter((part) => isNaN(Number(part)));
        return (
          hiddenPathParts.length === currentPathParts.length &&
          hiddenPathParts.every(
            (part, index) => part === currentPathParts[index]
          )
        );
      }
      return hiddenKey === key;
    });
  };

  // Helper function to check if a key should be disabled
  const isKeyDisabled = (path, key) => {
    const fullPath = path ? `${path} > ${key}` : key;
    return disabledKeys.some((disabledKey) => {
      if (disabledKey.includes(".")) {
        const disabledPathParts = disabledKey.split(".");
        const currentPathParts = fullPath.split(" > ");
        return disabledPathParts.every(
          (part, index) => currentPathParts[index] === part
        );
      }
      return disabledKey === key;
    });
  };

  const renderJsonNode = (data, path = "") => {
    const visibleKeys = Object.keys(data).filter(
      (key) => !isKeyHidden(path, key)
    );

    return visibleKeys.map((key) => {
      const value = data[key];
      const newPath = path ? `${path} > ${key}` : key;
      const hasChildren = typeof value === "object" && value !== null;
      const displayKey = Array.isArray(data)
        ? `${data[newPath]?.key || key}`
        : key;
      const isDisabled = isKeyDisabled(path, key);

      return (
        <Box
          key={newPath}
          sx={{
            pl: 2,
            borderLeft: "1px solid",
            borderColor: "divider",
            py: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: hasChildren ? "pointer" : "default",
            }}
          >
            {hasChildren ? (
              <IconButton size="small" onClick={() => toggleExpand(newPath)}>
                {expandedKeys[newPath] ? (
                  <ExpandMoreIcon />
                ) : (
                  <ChevronRightIcon />
                )}
              </IconButton>
            ) : (
              <Box sx={{ width: 24 }} />
            )}
            <Typography
              sx={{
                ml: 1,
                fontWeight: 600,
                minWidth: 170,
                maxWidth: 170,
                whiteSpace: "normal",
                wordWrap: "break-word",
                overflow: "hidden",
                padding: 1,
              }}
            >
              {displayKey.toCapitalized()}:
            </Typography>

            {!hasChildren && (
              <TextField
                inputRef={(el) => {
                  if (el) inputRefs.current[newPath] = el;
                }}
                value={value ?? ""}
                onChange={(e) => handleChange(newPath, e.target.value)}
                size="small"
                fullWidth
                disabled={isDisabled} // Disable input if key is in disabledKeys
              />
            )}
            {hasChildren && (
              <IconButton size="small" onClick={() => handleAddField(newPath)}>
                <AddIcon />
              </IconButton>
            )}
          </Box>
          {hasChildren && expandedKeys[newPath] && (
            <Box>{renderJsonNode(value, newPath)}</Box>
          )}
          {addingField === newPath && (
            <Grid
              container
              sx={{ mt: 2, p: 2, border: "1px solid grey", borderRadius: 1 }}
            >
              <FieldInput
                field={newField}
                onFieldChange={(key, value) =>
                  setNewField({ ...newField, [key]: value })
                }
                sx={{ mb: 2 }}
                error={fieldError}
                setError={setFieldError}
              />
              {newField.type === "object" && (
                <Box sx={{ width: "100%" }}>
                  {newObjectFields.map((field, index) => (
                    <RecursiveFieldEditor
                      key={index}
                      field={field}
                      onFieldChange={(updatedField) => {
                        const updatedFields = [...newObjectFields];
                        updatedFields[index] = updatedField;
                        setNewObjectFields(updatedFields);
                      }}
                      onDeleteField={() => {
                        const updatedFields = newObjectFields.filter(
                          (_, i) => i !== index
                        );
                        setNewObjectFields(updatedFields);
                      }}
                      onAddNestedField={() => {
                        const updatedFields = [...newObjectFields];
                        updatedFields[index].nestedFields = [
                          ...(updatedFields[index].nestedFields || []),
                          {
                            key: "",
                            type: "string",
                            value: "",
                            nestedFields: [],
                          },
                        ];
                        setNewObjectFields(updatedFields);
                      }}
                    />
                  ))}
                  <Button
                    onClick={() =>
                      setNewObjectFields([
                        ...newObjectFields,
                        {
                          key: "",
                          type: "string",
                          value: "",
                          nestedFields: [],
                        },
                      ])
                    }
                    size="small"
                    sx={{ mt: 1 }}
                  >
                    Add Field
                  </Button>
                </Box>
              )}
              {newField.type === "array" && (
                <Box sx={{ width: "100%" }}>
                  {newArrayObjects.map((objFields, objIndex) => (
                    <Box
                      key={objIndex}
                      sx={{
                        mt: 1,
                        p: 1,
                        border: "1px solid grey",
                        borderRadius: 1,
                      }}
                    >
                      {objFields.map((field, fieldIndex) => (
                        <RecursiveFieldEditor
                          key={fieldIndex}
                          field={field}
                          onFieldChange={(updatedField) => {
                            const updatedArrayObjects = [...newArrayObjects];
                            updatedArrayObjects[objIndex][fieldIndex] =
                              updatedField;
                            setNewArrayObjects(updatedArrayObjects);
                          }}
                          onDeleteField={() => {
                            const updatedArrayObjects = [...newArrayObjects];
                            updatedArrayObjects[objIndex] = updatedArrayObjects[
                              objIndex
                            ].filter((_, i) => i !== fieldIndex);
                            if (updatedArrayObjects[objIndex].length === 0) {
                              updatedArrayObjects.splice(objIndex, 1);
                            }
                            setNewArrayObjects(updatedArrayObjects);
                          }}
                          onAddNestedField={() => {
                            const updatedArrayObjects = [...newArrayObjects];
                            updatedArrayObjects[objIndex][
                              fieldIndex
                            ].nestedFields = [
                              ...(updatedArrayObjects[objIndex][fieldIndex]
                                .nestedFields || []),
                              {
                                key: "",
                                type: "string",
                                value: "",
                                nestedFields: [],
                              },
                            ];
                            setNewArrayObjects(updatedArrayObjects);
                          }}
                        />
                      ))}
                      <Button
                        onClick={() => {
                          const updatedArrayObjects = [...newArrayObjects];
                          updatedArrayObjects[objIndex].push({
                            key: "",
                            type: "string",
                            value: "",
                            nestedFields: [],
                          });
                          setNewArrayObjects(updatedArrayObjects);
                        }}
                        size="small"
                        sx={{ mt: 1 }}
                      >
                        Add Field to Object
                      </Button>
                    </Box>
                  ))}
                  <Button
                    onClick={() =>
                      setNewArrayObjects([
                        ...newArrayObjects,
                        [
                          {
                            key: "",
                            type: "string",
                            value: "",
                            nestedFields: [],
                          },
                        ],
                      ])
                    }
                    size="small"
                    sx={{ mt: 1 }}
                  >
                    Add New Object
                  </Button>
                </Box>
              )}
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "end",
                  marginTop: 5,
                }}
              >
                <Button
                  onClick={handleSaveField}
                  variant="contained"
                  size="small"
                  sx={{ mr: 1, mt: 1 }}
                >
                  Save
                </Button>
                <Button
                  onClick={() => setAddingField(null)}
                  size="small"
                  color="error"
                  sx={{ mt: 1 }}
                >
                  Cancel
                </Button>
              </div>
            </Grid>
          )}
        </Box>
      );
    });
  };

  const handleChange = (path, newValue) => {
    const updatedData = { ...editableData };
    const keys = path.split(" > ");
    let current = updatedData;
    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        current[key] = newValue;
      } else {
        if (!current[key]) current[key] = {};
        current = current[key];
      }
    });
    setEditableData(updatedData);
  };

  const toggleExpand = (key) => {
    setExpandedKeys((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getChangedObjects = (current, initial) => {
    const changed = {};

    Object.keys(current).forEach((key) => {
      if (JSON.stringify(current[key]) !== JSON.stringify(initial[key])) {
        changed[key] = current[key];
      }
    });

    Object.keys(initial).forEach((key) => {
      if (
        !changed[key] &&
        typeof initial[key] === "object" &&
        initial[key] !== null
      ) {
        changed[key] = initial[key];
      }
    });

    return changed;
  };

  const handleSave = () => {
    try {
      if (!initialDataRef.current) {
        throw new Error("No initial data available for comparison.");
      }

      const changedData = getChangedObjects(
        editableData,
        initialDataRef.current
      );

      if (Object.keys(changedData).length === 0) {
        setErrorMessage("No changes detected.");
        return;
      }
      onSaveWorkFlowData && onSaveWorkFlowData(changedData?.input);
      onClose();
    } catch (error) {
      setErrorMessage(error.message || "An error occurred while saving.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* <Box sx={{ display: "flex", flexDirection: "column" }}> */}
          <Typography variant="h6">{title}</Typography>
          {/* </Box> */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {step && (
              <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip label={step} size="small" sx={{ bgcolor: "#00ff94" }} />
              </Box>
            )}
            {/* <IconButton onClick={handleAddRootField} sx={{ mr: 1 }}>
              <AddIcon />
            </IconButton> 
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>*/}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "60vh", overflowY: "auto" }}>
        {renderJsonNode(editableData)}
        {addingField === "root" && (
          <Grid
            container
            sx={{ mt: 2, p: 2, border: "1px solid grey", borderRadius: 1 }}
          >
            <FieldInput
              field={newField}
              onFieldChange={(key, value) =>
                setNewField({ ...newField, [key]: value })
              }
              sx={{ mb: 2 }}
              error={fieldError}
              setError={setFieldError}
            />
            {newField.type === "object" && (
              <Box>
                {newObjectFields.map((field, index) => (
                  <RecursiveFieldEditor
                    key={index}
                    field={field}
                    onFieldChange={(updatedField) => {
                      const updatedFields = [...newObjectFields];
                      updatedFields[index] = updatedField;
                      setNewObjectFields(updatedFields);
                    }}
                    onDeleteField={() => {
                      const updatedFields = newObjectFields.filter(
                        (_, i) => i !== index
                      );
                      setNewObjectFields(updatedFields);
                    }}
                    onAddNestedField={() => {
                      const updatedFields = [...newObjectFields];
                      updatedFields[index].nestedFields = [
                        ...(updatedFields[index].nestedFields || []),
                        {
                          key: "",
                          type: "string",
                          value: "",
                          nestedFields: [],
                        },
                      ];
                      setNewObjectFields(updatedFields);
                    }}
                  />
                ))}
                <Button
                  onClick={() =>
                    setNewObjectFields([
                      ...newObjectFields,
                      { key: "", type: "string", value: "", nestedFields: [] },
                    ])
                  }
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Add Field
                </Button>
              </Box>
            )}
            {newField.type === "array" && (
              <Box sx={{ width: "100%" }}>
                {newArrayObjects.map((objFields, objIndex) => (
                  <Box
                    key={objIndex}
                    sx={{
                      mt: 1,
                      p: 1,
                      border: "1px solid grey",
                      borderRadius: 1,
                    }}
                  >
                    {objFields.map((field, fieldIndex) => (
                      <RecursiveFieldEditor
                        key={fieldIndex}
                        field={field}
                        onFieldChange={(updatedField) => {
                          const updatedArrayObjects = [...newArrayObjects];
                          updatedArrayObjects[objIndex][fieldIndex] =
                            updatedField;
                          setNewArrayObjects(updatedArrayObjects);
                        }}
                        onDeleteField={() => {
                          const updatedArrayObjects = [...newArrayObjects];
                          updatedArrayObjects[objIndex] = updatedArrayObjects[
                            objIndex
                          ].filter((_, i) => i !== fieldIndex);
                          if (updatedArrayObjects[objIndex].length === 0) {
                            updatedArrayObjects.splice(objIndex, 1);
                          }
                          setNewArrayObjects(updatedArrayObjects);
                        }}
                        onAddNestedField={() => {
                          const updatedArrayObjects = [...newArrayObjects];
                          updatedArrayObjects[objIndex][
                            fieldIndex
                          ].nestedFields = [
                            ...(updatedArrayObjects[objIndex][fieldIndex]
                              .nestedFields || []),
                            {
                              key: "",
                              type: "string",
                              value: "",
                              nestedFields: [],
                            },
                          ];
                          setNewArrayObjects(updatedArrayObjects);
                        }}
                      />
                    ))}
                    <Button
                      onClick={() => {
                        const updatedArrayObjects = [...newArrayObjects];
                        updatedArrayObjects[objIndex].push({
                          key: "",
                          type: "string",
                          value: "",
                          nestedFields: [],
                        });
                        setNewArrayObjects(updatedArrayObjects);
                      }}
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      Add Field to Object
                    </Button>
                  </Box>
                ))}
                <Button
                  onClick={() =>
                    setNewArrayObjects([
                      ...newArrayObjects,
                      [
                        {
                          key: "",
                          type: "string",
                          value: "",
                          nestedFields: [],
                        },
                      ],
                    ])
                  }
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Add New Object
                </Button>
              </Box>
            )}
            <div
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "end",
                marginTop: 5,
              }}
            >
              <Button
                onClick={handleSaveField}
                variant="contained"
                size="small"
                sx={{ mr: 1, mt: 1 }}
              >
                Save
              </Button>
              <Button
                onClick={() => setAddingField(null)}
                size="small"
                color="error"
                sx={{ mt: 1 }}
              >
                Cancel
              </Button>
            </div>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
        <Button color="error" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        message={errorMessage}
      />
    </Dialog>
  );
};

export default EditableHierarchicalJsonViewer;
